import { v4 as uuid } from 'uuid';

const VISITOR_KEY = 'sp_visitor';
const FIRST_TOUCH_KEY = 'sp_ft';
const LAST_TOUCH_KEY = 'sp_lt';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const readCookie = (name: string): string | undefined => {
  try {
    const prefix = `${name}=`;
    const value = document.cookie
      .split('; ')
      .find((item) => item.startsWith(prefix))
      ?.slice(prefix.length);

    return value ? decodeURIComponent(value) : undefined;
  } catch {
    return undefined;
  }
};

const writeCookie = (name: string, value: string): void => {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
  } catch {
    // Attribution must never block the application.
  }
};

const readStorage = (key: string): string | undefined => {
  try {
    return window.localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

const writeStorage = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // The cookie remains the fallback when storage is unavailable.
  }
};

export const getOrCreateAttributionVisitorId = (): string => {
  const stored = readStorage(VISITOR_KEY) ?? readCookie(VISITOR_KEY);
  if (stored) {
    writeStorage(VISITOR_KEY, stored);
    writeCookie(VISITOR_KEY, stored);

    return stored;
  }

  const visitorId = window.crypto?.randomUUID?.() ?? uuid();
  writeStorage(VISITOR_KEY, visitorId);
  writeCookie(VISITOR_KEY, visitorId);

  return visitorId;
};

// Invitation credentials and payment payloads must never reach attribution.
export const sanitizeAttributionUrl = (value: string): string => {
  const url = new URL(value);
  url.pathname = url.pathname.replace(/(\/scor\/shared|\/s)\/[^/]+\/?$/, '$1');
  ['token', 'code', 'invite_code', 'qr_payload'].forEach((key) => url.searchParams.delete(key));
  if (/^\/(scor|s)(\/|$)/.test(url.pathname)) url.hash = '';

  return url.href;
};

const getExternalReferrer = (): string | undefined => {
  if (!document.referrer) return undefined;
  try {
    const referrer = new URL(document.referrer);

    return referrer.host === window.location.host ? undefined : sanitizeAttributionUrl(referrer.href);
  } catch {
    return undefined;
  }
};

export const createAttributionTouch = (language: string): ApiAttributionTouchRequestData => {
  const url = new URL(sanitizeAttributionUrl(window.location.href));
  const params = url.searchParams;
  const touch: ApiAttributionTouchRequestData = {
    visitor_id: getOrCreateAttributionVisitorId(),
    source: params.get('utm_source') ?? undefined,
    medium: params.get('utm_medium') ?? undefined,
    campaign: params.get('utm_campaign') ?? undefined,
    content: params.get('utm_content') ?? undefined,
    term: params.get('utm_term') ?? undefined,
    click_id: params.get('yclid') ?? params.get('gclid') ?? params.get('fbclid') ?? undefined,
    ref_code: params.get('ref') ?? undefined,
    referrer_url: getExternalReferrer(),
    landing_url: url.href,
    platform: 'web',
    language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  const serializedTouch = JSON.stringify(touch);

  if (!readCookie(FIRST_TOUCH_KEY)) writeCookie(FIRST_TOUCH_KEY, serializedTouch);
  const hasCampaign = [
    touch.source,
    touch.medium,
    touch.campaign,
    touch.content,
    touch.term,
    touch.click_id,
    touch.ref_code,
    touch.referrer_url,
  ].some(Boolean);
  if (hasCampaign || !readCookie(LAST_TOUCH_KEY)) writeCookie(LAST_TOUCH_KEY, serializedTouch);

  return touch;
};
